import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faListUl,
  faCheckCircle,
  faClock,
  faEye,
  faFilePdf,
  faTrash,
  faArrowsRotate,
  faPlus,
  faBookmark,
  faSliders,
  faSearch,
  faXmark,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { evaluationService, AddressEvaluation } from '../services/evaluationService';
import { offerPlaygroundService } from '../services/offerPlaygroundService';
import { accountService, SkoolCommunityInfo, SubscriptionSummary } from '../services/accountService';
import AppSidebar from '../components/AppSidebar';
import SkoolCommunityCard from '../components/SkoolCommunityCard';
import SetPasswordBanner from '../components/SetPasswordBanner';
import PaymentRequiredBanner from '../components/PaymentRequiredBanner';
import SubscriptionUsageCard from '../components/SubscriptionUsageCard';
import WtiLoader from '../components/WtiLoader';
import { getLimitErrorMessage } from '../utils/limitErrorMessages';
import { formatAddressForDisplay } from '../components/AddressAutocomplete';
import toast from 'react-hot-toast';
import '../App.css';

function evaluationMatchesSearch(evalItem: AddressEvaluation, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const searchable = [
    evalItem.address.fullAddress,
    evalItem.address.street,
    evalItem.address.city,
    evalItem.address.state,
    evalItem.address.zipCode,
    evalItem.address.county,
    evalItem.address.neighborhood,
    evalItem.address.metroArea,
    evalItem.status,
    evalItem.propertyType?.replace(/_/g, ' '),
    formatAddressForDisplay(evalItem.address.fullAddress, evalItem.address),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const terms = q.split(/\s+/).filter(Boolean);
  return terms.every((term) => searchable.includes(term));
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [evaluations, setEvaluations] = useState<AddressEvaluation[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [skoolInfo, setSkoolInfo] = useState<SkoolCommunityInfo | null>(null);
  const [subscriptionSummary, setSubscriptionSummary] = useState<SubscriptionSummary | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [openingPlaygroundId, setOpeningPlaygroundId] = useState<string | null>(null);
  const [downloadingReportId, setDownloadingReportId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredEvaluations = useMemo(
    () => evaluations.filter((evalItem) => evaluationMatchesSearch(evalItem, searchQuery)),
    [evaluations, searchQuery],
  );

  const totalEvaluations = evaluations.length;
  const filteredCount = filteredEvaluations.length;
  const completedCount = evaluations.filter((e) => e.status === 'completed').length;
  const processingCount = evaluations.filter((e) => e.status === 'processing').length;

  const totalPages = Math.max(1, Math.ceil(filteredCount / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedEvaluations = filteredEvaluations.slice(startIndex, startIndex + pageSize);

  // Reset to page 1 when list length, filter, or pageSize changes
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [filteredCount, pageSize, totalPages, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    loadEvaluations();
    loadSkoolCommunity();
    loadSubscriptionSummary();
  }, []);

  const loadSubscriptionSummary = async () => {
    try {
      const summary = await accountService.getSubscriptionSummary();
      setSubscriptionSummary(summary);
    } catch {
      setSubscriptionSummary(null);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const loadSkoolCommunity = async () => {
    try {
      const info = await accountService.getSkoolCommunityInfo();
      setSkoolInfo(info);
    } catch {
      setSkoolInfo({ showSkoolCommunity: false, skoolCommunityUrl: null });
    }
  };

  const loadEvaluations = async () => {
    try {
      const data = await evaluationService.getEvaluations();
      setEvaluations(data);
    } catch (error) {
      toast.error(getLimitErrorMessage(error, 'Failed to load evaluations'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleToggleSave = async (evalItem: AddressEvaluation) => {
    try {
      if (evalItem.isSaved) {
        await evaluationService.unsaveEvaluation(evalItem._id);
        toast.success('Report removed from saved list');
      } else {
        await evaluationService.saveEvaluation(evalItem._id);
        toast.success('Report saved');
      }
      loadEvaluations();
      loadSubscriptionSummary();
    } catch (error) {
      toast.error(getLimitErrorMessage(error, 'Failed to update saved report'));
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this evaluation?')) {
      try {
        await evaluationService.deleteEvaluation(id);
        toast.success('Evaluation deleted');
        loadEvaluations();
      } catch (error) {
        toast.error('Failed to delete evaluation');
      }
    }
  };

  const handleOpenOfferPlayground = async (evaluationId: string) => {
    if (openingPlaygroundId) return;
    setOpeningPlaygroundId(evaluationId);
    try {
      const pg = await offerPlaygroundService.openForReport(evaluationId);
      window.open(`/offer-playground/${pg.id}`, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error(
        getLimitErrorMessage(error, 'Could not open Offer Playground. Ensure cash flow data is available.'),
      );
    } finally {
      setOpeningPlaygroundId(null);
    }
  };

  if (loading) {
    return <WtiLoader fullPage message="Loading dashboard…" />;
  }

  return (
    <div className="wti-shell">
      <AppSidebar />

      <div className="wti-main">
        <div className="container dashboard-page">
          <header className="dashboard-header">
            <div className="dashboard-title-block">
                  <h1>Where To Invest</h1>
              <p className="dashboard-subtitle">
                Evaluate and track addresses for your next investment move.
              </p>
              <p className="dashboard-welcome">Welcome, {user?.firstName}.</p>
            </div>
          </header>

          <SetPasswordBanner />
          <PaymentRequiredBanner summary={subscriptionSummary} />

          <section className="dashboard-summary-grid">
            <div className="dashboard-summary-card">
              <div className="dashboard-summary-header">
                <p className="dashboard-summary-label">Total evaluations</p>
                <span className="dashboard-summary-icon">
                  <FontAwesomeIcon icon={faListUl} />
                </span>
              </div>
              <p className="dashboard-summary-value">{totalEvaluations}</p>
            </div>
            <div className="dashboard-summary-card">
              <div className="dashboard-summary-header">
                <p className="dashboard-summary-label">Completed</p>
                <span className="dashboard-summary-icon dashboard-summary-icon--success">
                  <FontAwesomeIcon icon={faCheckCircle} />
                </span>
              </div>
              <p className="dashboard-summary-value dashboard-summary-value--success">
                {completedCount}
              </p>
            </div>
            <div className="dashboard-summary-card">
              <div className="dashboard-summary-header">
                <p className="dashboard-summary-label">Processing</p>
                <span className="dashboard-summary-icon dashboard-summary-icon--warning">
                  <FontAwesomeIcon icon={faClock} />
                </span>
              </div>
              <p className="dashboard-summary-value dashboard-summary-value--warning">
                {processingCount}
              </p>
            </div>
          </section>

          <SubscriptionUsageCard summary={subscriptionSummary} loading={subscriptionLoading} />

          {skoolInfo && <SkoolCommunityCard info={skoolInfo} />}

          <div className="card dashboard-table-card">
        <div className="dashboard-table-header">
          <div>
            <h2>Your address evaluations</h2>
            <p className="dashboard-table-subtitle">
              Recent addresses you&apos;ve evaluated. Open any row to see the full WTI dashboards.
            </p>
          </div>
              <div className="dashboard-table-header-actions">
                <button
                  className="btn btn-primary btn-sm"
                  title="Evaluate new address"
                  onClick={() => navigate('/evaluate')}
                >
                  <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />
                  Evaluate new address
                </button>
                <button
                  className={`btn btn-primary btn-sm dashboard-refresh-btn ${
                    refreshing ? 'dashboard-refresh-btn--spinning' : ''
                  }`}
                  title="Refresh evaluations"
                  onClick={() => {
                    setRefreshing(true);
                    loadEvaluations();
                  }}
                >
                  <FontAwesomeIcon icon={faArrowsRotate} />
                </button>
              </div>
        </div>

        {evaluations.length > 0 && (
          <div className="dashboard-search-row">
            <FontAwesomeIcon icon={faSearch} className="dashboard-search-icon" aria-hidden="true" />
            <input
              type="search"
              className="dashboard-search-input"
              placeholder="Search by address, city, state, zip, or status..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search evaluations"
            />
            {searchQuery.trim() && (
              <button
                type="button"
                className="btn btn-ghost dashboard-search-clear"
                title="Clear search"
                onClick={() => setSearchQuery('')}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            )}
          </div>
        )}

        {evaluations.length === 0 ? (
          <p className="dashboard-empty-state">No evaluations yet.</p>
        ) : filteredCount === 0 ? (
          <p className="dashboard-empty-state">
            No evaluations match &ldquo;{searchQuery.trim()}&rdquo;.
          </p>
        ) : (
          <>
          <div className="dashboard-table-wrapper">
            <table className="dashboard-table">
              <colgroup>
                <col className="colgroup-index" />
                <col className="colgroup-address" />
                <col className="colgroup-status" />
                <col className="colgroup-date" />
                <col className="colgroup-actions" />
              </colgroup>
              <thead>
                <tr>
                  <th className="col-index">#</th>
                  <th className="col-address">Address</th>
                  <th className="col-status">Status</th>
                  <th className="col-date">Date</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedEvaluations.map((evalItem, index) => (
                  <tr key={evalItem._id}>
                    <td className="col-index">{startIndex + index + 1}</td>
                    <td className="col-address">
                      {formatAddressForDisplay(evalItem.address.fullAddress, evalItem.address)}
                    </td>
                    <td className="col-status">
                      <span
                        className={`status-pill status-pill--${
                          evalItem.status === 'completed'
                            ? 'success'
                            : evalItem.status === 'processing'
                            ? 'warning'
                            : 'danger'
                        }`}
                      >
                        {evalItem.status}
                      </span>
                    </td>
                    <td className="col-date">{new Date(evalItem.createdAt).toLocaleDateString()}</td>
                    <td className="col-actions">
                      <div className="dashboard-row-actions">
                        {evalItem.status === 'completed' ? (
                          <>
                            <button
                              className="btn btn-ghost dashboard-action-icon-btn dashboard-action-icon-btn--view"
                              title="View dashboards"
                              onClick={() => navigate(`/evaluation/${evalItem._id}`)}
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </button>
                            <button
                              className="btn btn-ghost dashboard-action-icon-btn dashboard-action-icon-btn--pdf"
                              title={
                                downloadingReportId === evalItem._id
                                  ? 'Downloading…'
                                  : 'Download PDF'
                              }
                              disabled={downloadingReportId === evalItem._id}
                              aria-busy={downloadingReportId === evalItem._id}
                              onClick={async () => {
                                if (downloadingReportId) return;
                                setDownloadingReportId(evalItem._id);
                                try {
                                  const blob = await evaluationService.generateReport(evalItem._id);
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `WTI_Report_${evalItem._id}.pdf`;
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                  toast.success('Report downloaded');
                                  loadSubscriptionSummary();
                                } catch (error) {
                                  toast.error(getLimitErrorMessage(error, 'Failed to generate report'));
                                } finally {
                                  setDownloadingReportId(null);
                                }
                              }}
                            >
                              <FontAwesomeIcon
                                icon={
                                  downloadingReportId === evalItem._id ? faSpinner : faFilePdf
                                }
                                spin={downloadingReportId === evalItem._id}
                              />
                            </button>
                            <button
                              className="btn btn-ghost dashboard-action-icon-btn dashboard-action-icon-btn--playground"
                              title="Open Offer Playground"
                              disabled={openingPlaygroundId === evalItem._id}
                              onClick={() => handleOpenOfferPlayground(evalItem._id)}
                            >
                              <FontAwesomeIcon icon={faSliders} />
                            </button>
                            <button
                              className={`btn btn-ghost dashboard-action-icon-btn ${
                                evalItem.isSaved ? 'dashboard-action-icon-btn--saved' : ''
                              }`}
                              title={evalItem.isSaved ? 'Remove from saved' : 'Save report'}
                              onClick={() => handleToggleSave(evalItem)}
                            >
                              <FontAwesomeIcon icon={faBookmark} />
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="btn btn-ghost btn-ghost--placeholder">View</span>
                            <span className="btn btn-ghost btn-ghost--placeholder">PDF</span>
                          </>
                        )}
                        <button
                          className="btn btn-danger dashboard-action-icon-btn dashboard-action-icon-btn--delete"
                          title="Delete evaluation"
                          onClick={() => handleDelete(evalItem._id)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="dashboard-pagination">
            <div className="dashboard-pagination-info">
              <span className="dashboard-pagination-showing">
                Showing {filteredCount === 0 ? 0 : startIndex + 1}&ndash;{Math.min(startIndex + pageSize, filteredCount)} of {filteredCount}
                {searchQuery.trim() ? ` (filtered from ${totalEvaluations})` : ''}
              </span>
              <label className="dashboard-pagination-size">
                Rows per page
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="dashboard-pagination-select"
                >
                  {[5, 10, 25, 50].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="dashboard-pagination-controls">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span className="dashboard-pagination-pages">
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
          </>
        )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
